import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProfile } from "@/hooks/useProfile";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useNativeCamera } from "@/hooks/useNativeCamera";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Capacitor } from "@capacitor/core";
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from "capacitor-native-settings";
import {
  ArrowLeft,
  Send,
  Loader2,
  Languages,
  Users,
  Info,
  Search,
  X,
  Paperclip,
  File as FileIcon,
  Camera,
  Mic,
  Square,
  Reply,
  Forward,
  Pencil,
  Trash2,
  ImageIcon,
  Bell,
  BellOff,
  Images,
  Star,
  Ban,
  ShieldCheck,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { GroupMembersDialog } from "@/components/GroupMembersDialog";
import { MediaGalleryDialog } from "@/components/MediaGalleryDialog";
import { StarredMessagesDialog } from "@/components/StarredMessagesDialog";
import { MessageReactions } from "@/components/MessageReactions";
import { AttachmentDisplay } from "@/components/AttachmentDisplay";
import { convertAudioBlobToWavBlob } from "@/utils/audioToWav";
import { MessageStatus } from "@/components/MessageStatus";
import { ForwardMessageDialog } from "@/components/ForwardMessageDialog";
import { LinkPreview } from "@/components/LinkPreview";
import { MessageActions } from "@/components/MessageActions";
import { GifPicker } from "@/components/GifPicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useBlockedUsers } from "@/hooks/useBlockedUsers";

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  original_text: string;
  translated_texts: any;
  sender_language: string;
  attachment_url?: string;
  created_at: string;
  is_read?: boolean;
  reply_to_id?: string | null;
  sender?: {
    name: string;
    profile_image?: string;
  };
  reactions?: Array<{
    id: string;
    user_id: string;
    reaction: string;
  }>;
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English", es: "Spanish", fr: "French", de: "German",
  it: "Italian", pt: "Portuguese", ru: "Russian", ja: "Japanese",
  ko: "Korean", zh: "Chinese", ar: "Arabic", hi: "Hindi",
};

const ChatScreen = () => {
const { chatId } = useParams();
  const navigate = useNavigate();
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const { user, subscribed, inTrial } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [messageOriginalOverrides, setMessageOriginalOverrides] = useState<
    Set<string>
  >(new Set());
  const [otherParticipant, setOtherParticipant] = useState<any>(null);
  const [chatInfo, setChatInfo] = useState<any>(null);
  const [allParticipants, setAllParticipants] = useState<any[]>([]);
  const senderProfileCacheRef = useRef<Record<string, { name: string; profile_image?: string }>>({});
  const messagesFetchRequestRef = useRef(0);
  const optimisticMessagesRef = useRef<Message[]>([]);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [showStarred, setShowStarred] = useState(false);
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set());
  const { isBlocked, block, unblock } = useBlockedUsers();
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [deletingMessage, setDeletingMessage] = useState<Message | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [longPressMessage, setLongPressMessage] = useState<Message | null>(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const translatingMessageIdsRef = useRef<Set<string>>(new Set());
  const [translatingMessageIds, setTranslatingMessageIds] = useState<Set<string>>(new Set());
  const [isMuted, setIsMuted] = useState(false);
  const translationErrorShownRef = useRef(false);
  const { uploadMultipleFiles, uploading, uploadProgress, getFileType } =
    useFileUpload();
  const { isNative, pickImage, loading: cameraLoading } = useNativeCamera();

  const handleVoiceRecordingComplete = async (
    audioBlob: Blob,
    mimeType: string
  ) => {
    if (!chatId || !user || !profile) return;

    setIsUploadingVoice(true);

    const rawMimeBase = (mimeType.split(";")[0] || "").trim();
    // Some Android WebViews/plugins can yield an empty MIME type; fall back to a sensible audio type.
    const mimeBase = rawMimeBase || "audio/aac";

    // IMPORTANT: Native recordings (Android AAC, iOS M4A/CAF) often have formats that
    // Whisper handles inconsistently. We proactively transcode to WAV (PCM) for reliability.
    // This ensures consistent transcription quality across all device types.
    let finalBlob: Blob = audioBlob;
    let finalMime: string = mimeBase;
    try {
      const lower = mimeBase.toLowerCase();
      const isNativePlatform = Capacitor.isNativePlatform();
      const platformName = Capacitor.getPlatform();
      
      // Transcode both Android AND iOS native formats to WAV for consistent Whisper results
      // On native platforms, always transcode unless it's already WAV - the MIME type 
      // reported by plugins is often unreliable (e.g., iOS may report as webm)
      const isAlreadyWav = lower.includes("wav");
      const shouldTranscodeToWav = !isAlreadyWav && (
        isNativePlatform ||             // Always transcode on native platforms
        lower.includes("aac") ||
        lower.includes("mp4a") ||
        lower.includes("x-hx-aac-adts") ||
        lower.includes("m4a") ||        // iOS native format
        lower.includes("x-m4a") ||      // iOS alternate
        lower.includes("mp4") ||        // iOS can report as mp4
        lower.includes("caf") ||        // iOS Core Audio Format
        lower.includes("x-caf")         // iOS CAF alternate
      );

      console.log("[VoiceMessage] Transcode check:", { 
        lower, 
        isNativePlatform, 
        platformName,
        isAlreadyWav,
        shouldTranscodeToWav 
      });

      if (shouldTranscodeToWav) {
        console.log("[VoiceMessage] Transcoding to WAV for better Whisper compatibility...");
        finalBlob = await convertAudioBlobToWavBlob(audioBlob);
        finalMime = "audio/wav";
        console.log("[VoiceMessage] Transcoding complete, new size:", finalBlob.size);
      }
    } catch (e) {
      console.warn(
        "[VoiceMessage] WAV transcode failed; uploading original audio instead:",
        e
      );
      finalBlob = audioBlob;
      finalMime = mimeBase;
    }

    // Map MIME types to file extensions - handle native Android/iOS formats
    const getAudioExtension = (mime: string): string => {
      // Handle WAV first (from transcoding)
      if (mime.includes("wav")) return "wav";
      // Android can report AAC as "audio/mp4a-latm" or similar
      if (mime.includes("mp4a")) return "aac";
      if (mime.includes("aac")) return "aac";
      if (mime.includes("m4a") || mime.includes("x-m4a")) return "m4a";
      if (mime.includes("3gpp")) return "3gp";
      if (mime.includes("amr")) return "amr";
      if (mime.includes("mp4")) return "mp4";
      if (mime.includes("mpeg")) return "mp3";
      if (mime.includes("ogg")) return "ogg";
      return "webm";
    };
    const extension = getAudioExtension(finalMime);

    const audioFile = new File([finalBlob], `voice-message.${extension}`, {
      type: finalMime,
    });

    console.log("[VoiceMessage] Recording complete:", {
      chatId,
      mimeType,
      mimeBase,
      finalMime,
      extension,
      size: audioFile.size,
      name: audioFile.name,
    });

    try {
      const attachmentUrls = await uploadMultipleFiles([audioFile], chatId);
      console.log("[VoiceMessage] uploadMultipleFiles result:", attachmentUrls);

      if (attachmentUrls.length === 0) {
        throw new Error("Upload returned no attachment URLs");
      }

      const { error: insertError } = await supabase.from("messages").insert({
        chat_id: chatId,
        sender_id: user.id,
        original_text: "🎤 Voice message",
        sender_language: profile.preferred_language || language,
        attachment_url: attachmentUrls[0],
      });

      if (insertError) {
        console.error("[VoiceMessage] Message insert failed:", insertError);
        throw insertError;
      }
      trackEvent('message_sent', { type: 'voice' });

      // Immediately refresh messages so the voice message appears without waiting for realtime
      await fetchMessages();
      scrollToBottom();

      // Push notification is now sent automatically via DB trigger on messages INSERT
    } catch (err: any) {
      console.error("[VoiceMessage] Failed to send voice message:", err);
      toast({
        title: t("error"),
        description: err?.message || "Failed to send voice message",
        variant: "destructive",
      });
    } finally {
      setIsUploadingVoice(false);
    }
  };

  const openAppSettings = async () => {
    try {
      if (!Capacitor.isNativePlatform()) {
        toast({
          title: t("error"),
          description: "Please enable microphone permission in your browser settings.",
          variant: "destructive",
        });
        return;
      }

      const platform = Capacitor.getPlatform();

      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });

      // Note: AndroidSettings.ApplicationDetails also works when platform is iOS
      // but we keep both options for clarity.
      if (platform !== "android" && platform !== "ios") {
        // Shouldn't happen in Capacitor, but keep a safe fallback.
        throw new Error(`Unsupported platform: ${platform}`);
      }
    } catch (error) {
      console.error("Error opening app settings:", error);
      toast({
        title: "Open Settings Manually",
        description:
          "Go to Settings > Apps > Translatify Chat > Permissions > Microphone",
        variant: "destructive",
        duration: 10000,
      });
    }
  };

  const { isRecording, startRecording, stopRecording, cancelRecording } =
    useVoiceRecording({
      onRecordingComplete: handleVoiceRecordingComplete,
      onError: (error) => {
        if (error === "PERMISSION_DENIED") {
          toast({
            title: t("error"),
            description:
              "Microphone permission is required to record voice messages. Please enable it in app settings.",
            variant: "destructive",
            action: (
              <ToastAction altText="Open Settings" onClick={openAppSettings}>
                Open Settings
              </ToastAction>
            ),
          });
          return;
        }

        if (error === "MICROPHONE_UNAVAILABLE") {
          toast({
            title: t("error"),
            description:
              "Your microphone is currently unavailable (it may be in use by another app). Close other apps using the mic and try again.",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: t("error"),
          description: error,
          variant: "destructive",
        });
      },
    });

  useEffect(() => {
    if (!user || !chatId) return;

    setLoading(true);
    let initialLoadTriggered = false;
    const loadInitialData = () => {
      if (initialLoadTriggered) return;
      initialLoadTriggered = true;
      void fetchMessages();
    };

    void fetchChatInfo();
    void fetchAllParticipants();
    void fetchStarredForChat();

    const messagesChannel = supabase
      .channel(`chat-${chatId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `chat_id=eq.${chatId}`,
        },
        async (payload) => {
          await fetchMessages();
          if (payload.new && payload.new.sender_id !== user.id) {
            await markMessagesAsRead();
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          loadInitialData();
          return;
        }

        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          void fetchMessages();
        }
      });

    const reactionsChannel = supabase
      .channel(`reactions-${chatId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_reactions",
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    const typingChannel = supabase
      .channel(`typing-${chatId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "typing_indicators",
          filter: `chat_id=eq.${chatId}`,
        },
        () => {
          fetchTypingIndicators();
        }
      )
      .subscribe();

    const fallbackLoadTimer = setTimeout(loadInitialData, 1500);

    return () => {
      clearTimeout(fallbackLoadTimer);
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(reactionsChannel);
      supabase.removeChannel(typingChannel);
      updateTypingIndicator(false);
    };
  }, [user, chatId]);

  useEffect(() => {
    if (loading || messages.length === 0) return;

    // Check if we should scroll to a specific message from search
    const params = new URLSearchParams(window.location.search);
    const targetMessageId = params.get('messageId');

    if (targetMessageId) {
      const timer = setTimeout(() => {
        const el = document.querySelector(`[data-message-id="${targetMessageId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightMessageId(targetMessageId);
          setTimeout(() => setHighlightMessageId(null), 2500);
        }
        // Clean the URL
        window.history.replaceState({}, '', window.location.pathname);
      }, 100);
      return () => clearTimeout(timer);
    }

    // Use multiple attempts to ensure scroll works after DOM renders
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 100);
    const t2 = setTimeout(scrollToBottom, 300);
    const t3 = setTimeout(scrollToBottom, 600);
    const raf = requestAnimationFrame(scrollToBottom);

    // On-demand translation of older messages (translateMissingMessages)
    // keeps mutating message bubbles above the fold for several seconds
    // after open. Each of those height changes is a target for the
    // browser's native CSS scroll anchoring, which shifts scrollTop to
    // "compensate" — individually small, but compounding across a couple
    // dozen staggered translation updates into a large jump away from the
    // bottom. We already anchor to bottom ourselves, so disable the
    // browser's competing heuristic for this viewport.
    const viewportEl = scrollAreaRef.current?.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]"
    );
    if (viewportEl) {
      viewportEl.style.overflowAnchor = "none";
    }

    // Attachments, link previews, and translations can finish loading well
    // after the fixed-delay attempts above, growing the content and leaving
    // the view short of the real bottom. Re-anchor on every size change for
    // a few seconds while this chat's content is still settling in. Observe
    // our own messages-list wrapper directly (not Radix's internal viewport
    // child), since that internal structure isn't guaranteed and rendering
    // it out on WKWebView left this unobserved there.
    let resizeObserver: ResizeObserver | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    if (messagesListRef.current) {
      resizeObserver = new ResizeObserver(() => scrollToBottom());
      resizeObserver.observe(messagesListRef.current);
      settleTimer = setTimeout(() => resizeObserver?.disconnect(), 8000);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      cancelAnimationFrame(raf);
      if (settleTimer) clearTimeout(settleTimer);
      resizeObserver?.disconnect();
    };
  }, [loading, messages.length]);

  const scrollToBottom = () => {
    const root = scrollAreaRef.current;
    const viewport = root?.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]"
    );

    if (viewport) {
      // Force layout recalculation before scrolling
      void viewport.scrollHeight;
      viewport.scrollTop = viewport.scrollHeight;
    }

    // Belt and suspenders: on iOS WKWebView the inner viewport can end up
    // maxed out while the outer page itself is still scrollable (dynamic
    // viewport/safe-area quirks), clipping the last bit of content below
    // the fold. scrollIntoView walks and scrolls every scrollable ancestor,
    // inner viewport included, so this covers both cases.
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  };

  const fetchStarredForChat = async () => {
    if (!user || !chatId) return;
    try {
      const { data: messageRows } = await supabase
        .from("messages")
        .select("id")
        .eq("chat_id", chatId);
      const ids = (messageRows || []).map((m: any) => m.id);
      if (ids.length === 0) {
        setStarredIds(new Set());
        return;
      }
      const { data: stars, error } = await (supabase as any)
        .from("starred_messages")
        .select("message_id")
        .eq("user_id", user.id)
        .in("message_id", ids);
      if (error) {
        console.error("[ChatScreen] fetchStarred error:", error);
        return;
      }
      setStarredIds(new Set((stars || []).map((s: any) => s.message_id)));
    } catch (err) {
      console.error("[ChatScreen] fetchStarred unexpected error:", err);
    }
  };

  const toggleStarMessage = async (messageId: string) => {
    if (!user) return;
    const wasStarred = starredIds.has(messageId);
    // Optimistic update
    setStarredIds((prev) => {
      const next = new Set(prev);
      if (wasStarred) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
    try {
      if (wasStarred) {
        const { error } = await (supabase as any)
          .from("starred_messages")
          .delete()
          .eq("user_id", user.id)
          .eq("message_id", messageId);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from("starred_messages")
          .insert({ user_id: user.id, message_id: messageId });
        if (error) throw error;
        toast({ title: t("success") || "Starred", description: "Message starred" });
      }
    } catch (err) {
      console.error("[ChatScreen] toggleStar error:", err);
      // Revert
      setStarredIds((prev) => {
        const next = new Set(prev);
        if (wasStarred) next.add(messageId);
        else next.delete(messageId);
        return next;
      });
      toast({
        title: t("error") || "Error",
        description: "Could not update star",
        variant: "destructive",
      });
    }
  };

  const fetchMessages = async () => {
    if (!chatId) return;

    const requestId = ++messagesFetchRequestRef.current;

    try {
      const { data, error } = await supabase
        .from("messages")
        .select(
          `
          *,
          reactions:message_reactions (
            id,
            user_id,
            reaction
          )
        `
        )
        .eq("chat_id", chatId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (requestId !== messagesFetchRequestRef.current) return;
      
      // Fetch sender profiles using cache + RPC
      const senderIds = [...new Set((data || []).map(m => m.sender_id))];
      const uncachedIds = senderIds.filter(id => !senderProfileCacheRef.current[id]);
      
      if (uncachedIds.length > 0) {
        await Promise.all(
          uncachedIds.map(async (senderId) => {
            if (senderId === user?.id) {
              const { data: ownProfile } = await supabase
                .from('profiles')
                .select('name, profile_image')
                .eq('id', senderId)
                .single();
              if (ownProfile) senderProfileCacheRef.current[senderId] = ownProfile;
            } else {
              const { data: profileData } = await supabase
                .rpc('get_chat_participant_profile', { _profile_id: senderId });
              if (profileData && profileData.length > 0) {
                senderProfileCacheRef.current[senderId] = {
                  name: profileData[0].name,
                  profile_image: profileData[0].profile_image,
                };
              }
            }
          })
        );
      }
      
      const messagesWithSenders = (data || []).map(m => ({
        ...m,
        sender: senderProfileCacheRef.current[m.sender_id] || { name: 'Unknown' },
      }));

      const persistedPool = [...messagesWithSenders];
      const unresolvedOptimistic = optimisticMessagesRef.current.filter((optimistic) => {
        const matchingIndex = persistedPool.findIndex((persisted) => {
          const sameSender = persisted.sender_id === optimistic.sender_id;
          const sameChat = persisted.chat_id === optimistic.chat_id;
          const sameText = persisted.original_text === optimistic.original_text;
          const sameAttachment =
            (persisted.attachment_url || null) === (optimistic.attachment_url || null);
          const sameReply =
            (persisted.reply_to_id || null) === (optimistic.reply_to_id || null);
          const optimisticTime = new Date(optimistic.created_at).getTime();
          const persistedTime = new Date(persisted.created_at).getTime();
          const closeInTime =
            Number.isFinite(optimisticTime) &&
            Number.isFinite(persistedTime) &&
            Math.abs(optimisticTime - persistedTime) <= 120000;

          return (
            sameSender &&
            sameChat &&
            sameText &&
            sameAttachment &&
            sameReply &&
            closeInTime
          );
        });

        if (matchingIndex >= 0) {
          persistedPool.splice(matchingIndex, 1);
          return false;
        }

        return true;
      });

      optimisticMessagesRef.current = unresolvedOptimistic;

      const mergedMessages = [...messagesWithSenders, ...unresolvedOptimistic].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      
      setMessages(mergedMessages);

      if (user) {
        await markMessagesAsRead();
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      if (requestId === messagesFetchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const markMessagesAsRead = async () => {
    if (!chatId || !user) return;

    try {
      const { error } = await supabase.rpc('mark_chat_messages_read', {
        _chat_id: chatId,
      });

      if (error) {
        console.error("Error marking messages as read:", error);
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const fetchChatInfo = async () => {
    if (!chatId) return;

    try {
      const { data } = await supabase
        .from("chats")
        .select("*")
        .eq("id", chatId)
        .single();

      if (data) {
        setChatInfo(data);

        // Load own mute status from chat_participants
        if (user) {
          const { data: selfParticipant } = await supabase
            .from("chat_participants")
            .select("is_muted")
            .eq("chat_id", chatId)
            .eq("user_id", user.id)
            .maybeSingle();
          setIsMuted(selfParticipant?.is_muted || false);
        }

        if (!data.is_group) {
          // Get other participant's user_id first
          const { data: otherParticipantData } = await supabase
            .from("chat_participants")
            .select("user_id")
            .eq("chat_id", chatId)
            .neq("user_id", user?.id)
            .maybeSingle();

          if (otherParticipantData) {
            // Use RPC function to get profile (bypasses RLS restrictions)
            const { data: profileData } = await supabase
              .rpc('get_chat_participant_profile', { _profile_id: otherParticipantData.user_id });
            
            if (profileData && profileData.length > 0) {
              setOtherParticipant(profileData[0]);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching chat info:", error);
    }
  };

  const toggleMuteChat = async () => {
    if (!chatId || !user) return;
    try {
      const { error } = await supabase
        .from("chat_participants")
        .update({ is_muted: !isMuted })
        .eq("chat_id", chatId)
        .eq("user_id", user.id);
      if (error) throw error;
      setIsMuted((prev) => !prev);
    } catch (error) {
      console.error("Error toggling mute:", error);
    }
  };

  const fetchAllParticipants = async () => {
    if (!chatId || !user) return;

    try {
      // First get all participant user_ids
      const { data: participantRows } = await supabase
        .from("chat_participants")
        .select("user_id")
        .eq("chat_id", chatId);

      if (!participantRows || participantRows.length === 0) {
        setAllParticipants([]);
        return;
      }

      // Use the security definer RPC to fetch each profile (bypasses RLS)
      const profilePromises = participantRows.map((p) =>
        supabase.rpc("get_chat_participant_profile", { _profile_id: p.user_id })
      );

      const profileResults = await Promise.all(profilePromises);
      const profiles = profileResults
        .filter((r) => !r.error && r.data && r.data.length > 0)
        .map((r) => ({
          id: r.data![0].id,
          name: r.data![0].name,
          profile_image: r.data![0].profile_image,
          preferred_language: r.data![0].preferred_language,
        }));

      setAllParticipants(profiles);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  const fetchTypingIndicators = async () => {
    if (!chatId || !user) return;

    try {
      const { data } = await supabase
        .from("typing_indicators")
        .select("user_id, profiles (name)")
        .eq("chat_id", chatId)
        .eq("is_typing", true)
        .neq("user_id", user.id);

      setTypingUsers(
        data?.map((d: any) => d.profiles?.name).filter(Boolean) || []
      );
    } catch (error) {
      console.error("Error fetching typing indicators:", error);
    }
  };

  const updateTypingIndicator = async (isTyping: boolean) => {
    if (!chatId || !user) return;

    try {
      const { data: existing } = await supabase
        .from("typing_indicators")
        .select("id")
        .eq("chat_id", chatId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("typing_indicators")
          .update({
            is_typing: isTyping,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
      } else {
        await supabase.from("typing_indicators").insert({
          chat_id: chatId,
          user_id: user.id,
          is_typing: isTyping,
          updated_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error updating typing indicator:", error);
    }
  };

  const handleTyping = () => {
    updateTypingIndicator(true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      updateTypingIndicator(false);
    }, 3000);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      (!newMessage.trim() && selectedFiles.length === 0) ||
      !chatId ||
      !user ||
      !profile
    )
      return;

    setSending(true);
    updateTypingIndicator(false).catch(() => {});
    const tempMessageIds: string[] = [];

    try {
      const { data: isParticipant, error: participantError } =
        await supabase.rpc("is_chat_participant", {
          _chat_id: chatId,
          _user_id: user.id,
        });

      if (participantError) throw participantError;
      if (!isParticipant) {
        toast({
          title: t("error"),
          description: `You can't send messages in this chat (not a participant).`,
          variant: "destructive",
        });
        return;
      }

      const attachmentUrls =
        selectedFiles.length > 0
          ? await uploadMultipleFiles(selectedFiles, chatId)
          : [];

      if (selectedFiles.length > 0 && attachmentUrls.length === 0) {
        setSending(false);
        return;
      }

      const messageText =
        newMessage.trim() ||
        (selectedFiles.length > 0
          ? `${t("send")} ${selectedFiles.length} file${
              selectedFiles.length > 1 ? "s" : ""
            }`
          : "");

      const textToTranslate = newMessage.trim();

      // Determine target languages for translation
      const participantsForTranslation = allParticipants.length
        ? allParticipants
        : (
            await supabase
              .from("chat_participants")
              .select(`profiles (id, preferred_language)`)
              .eq("chat_id", chatId)
          ).data
            ?.map((row: any) => row.profiles)
            .filter(Boolean) ?? [];

      const targetLanguages = [
        ...new Set(
          participantsForTranslation
            .filter((p: any) => Boolean(p?.id && p?.preferred_language))
            .filter(
              (p: any) =>
                p.id !== user.id &&
                p.preferred_language !== profile.preferred_language
            )
            .map((p: any) => p.preferred_language)
        ),
      ];

      // Insert message immediately (optimistic) without waiting for translation
      const senderLang = profile.preferred_language ?? language;
      const replyId = replyToMessage?.id || null;
      const optimisticBase = {
        chat_id: chatId,
        sender_id: user.id,
        original_text: messageText,
        translated_texts: {},
        sender_language: senderLang,
        created_at: new Date().toISOString(),
        reply_to_id: replyId,
        sender: {
          name: profile.name,
          profile_image: profile.profile_image || undefined,
        },
        reactions: [],
      };

      const attachmentPayloads = attachmentUrls.length > 0 ? attachmentUrls : [null];
      const optimisticMessages: Message[] = attachmentPayloads.map((url) => {
        const tempId = `temp-${crypto.randomUUID()}`;
        tempMessageIds.push(tempId);

        return {
          id: tempId,
          attachment_url: url || undefined,
          ...optimisticBase,
        };
      });

      optimisticMessagesRef.current = [
        ...optimisticMessagesRef.current,
        ...optimisticMessages,
      ];

      setMessages((prev) =>
        [...prev, ...optimisticMessages].sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
      );
      scrollToBottom();

      if (attachmentUrls.length > 1) {
        for (const url of attachmentUrls) {
          const { error } = await supabase.from("messages").insert({
            chat_id: chatId,
            sender_id: user.id,
            original_text: messageText,
            translated_texts: {},
            sender_language: senderLang,
            attachment_url: url,
            reply_to_id: replyId,
          });
          if (error) throw error;
        }
      } else {
        const { error } = await supabase.from("messages").insert({
          chat_id: chatId,
          sender_id: user.id,
          original_text: messageText,
          translated_texts: {},
          sender_language: senderLang,
          attachment_url: attachmentUrls[0] || null,
          reply_to_id: replyId,
        });
        if (error) throw error;
      }

      trackEvent('message_sent', {
        type: attachmentUrls.length > 0 ? 'attachment' : 'text',
        attachment_count: attachmentUrls.length,
        has_text: !!messageText,
      });

      // Only clear composer after successful insert(s)
      setNewMessage("");
      setSelectedFiles([]);
      setReplyToMessage(null);

      // Ensure sender sees their message instantly even if realtime is delayed
      await fetchMessages();
      scrollToBottom();

      // Fire-and-forget: translate in background and update the message
      if (targetLanguages.length > 0 && textToTranslate) {
        (async () => {
          try {
            const {
              data: { session },
            } = await supabase.auth.getSession();
            const accessToken = session?.access_token;

            const { data: translationData, error: translationError } =
              await supabase.functions.invoke("translate-message", {
                headers: accessToken
                  ? { Authorization: `Bearer ${accessToken}` }
                  : undefined,
                body: {
                  text: textToTranslate,
                  sourceLanguage: senderLang,
                  targetLanguages,
                },
              });

            if (translationError) {
              console.error("[ChatScreen] Background translation error:", translationError);
              return;
            }

            if (translationData?.translations) {
              trackEvent('translation_used', {
                type: 'text',
                source_language: senderLang,
                target_count: targetLanguages.length,
              });
              // Update the message(s) in DB with translations
              // Find the messages we just inserted (most recent from this user in this chat)
              const { data: recentMsgs } = await supabase
                .from("messages")
                .select("id")
                .eq("chat_id", chatId)
                .eq("sender_id", user.id)
                .eq("original_text", messageText)
                .order("created_at", { ascending: false })
                .limit(attachmentUrls.length > 1 ? attachmentUrls.length : 1);

              if (recentMsgs) {
                await Promise.all(
                  recentMsgs.map((msg) =>
                    supabase
                      .from("messages")
                      .update({ translated_texts: translationData.translations })
                      .eq("id", msg.id)
                  )
                );
              }
            }
          } catch (err) {
            console.error("[ChatScreen] Background translation exception:", err);
          }
        })();
      }

      // Push notification is now sent automatically via DB trigger on messages INSERT
    } catch (error: any) {
      if (tempMessageIds.length > 0) {
        optimisticMessagesRef.current = optimisticMessagesRef.current.filter(
          (message) => !tempMessageIds.includes(message.id)
        );
        setMessages((prev) =>
          prev.filter((message) => !tempMessageIds.includes(message.id))
        );
      }

      const errorDetails = (() => {
        try {
          if (!error) return "";
          if (typeof error === "string") return error;
          const code = error.code ? `code=${error.code}` : "";
          const message = error.message ? `message=${error.message}` : "";
          const hint = error.hint ? `hint=${error.hint}` : "";
          return [code, message, hint].filter(Boolean).join(" ");
        } catch {
          return "";
        }
      })();

      console.error("Error sending message:", error);
      toast({
        title: t("error"),
        description: errorDetails
          ? `${t("tryAgain")} (${errorDetails})`
          : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNativeImagePick = async () => {
    try {
      const file = await pickImage();
      if (file) {
        setSelectedFiles((prev) => [...prev, file]);
      }
    } catch (error: any) {
      console.error("Error picking image:", error);
      toast({
        title: t("error"),
        description: t("tryAgain"),
        variant: "destructive",
      });
    }
  };

  const handleGifSelect = async (gifUrl: string) => {
    if (!chatId || !user || !profile) return;
    setShowGifPicker(false);
    setSending(true);
    try {
      const { error } = await supabase.from("messages").insert({
        chat_id: chatId,
        sender_id: user.id,
        original_text: "GIF",
        sender_language: profile.preferred_language || language,
        attachment_url: gifUrl,
      });
      if (error) throw error;
      trackEvent('message_sent', { type: 'gif' });
      await fetchMessages();
      scrollToBottom();

      // Push notification is now sent automatically via DB trigger on messages INSERT
    } catch (error: any) {
      console.error("Error sending GIF:", error);
      toast({ title: t("error"), description: t("tryAgain"), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const getMessageText = (message: Message, forceOriginal = false) => {
    if (message.sender_id === user?.id) {
      return message.original_text;
    }

    const hasOverride = messageOriginalOverrides.has(message.id);
    const showOriginalForThis = forceOriginal || hasOverride;

    if (!autoTranslate || showOriginalForThis) {
      return message.original_text;
    }

    const preferredLang = profile?.preferred_language ?? language;

    if (
      preferredLang &&
      message.translated_texts &&
      message.translated_texts[preferredLang]
    ) {
      return message.translated_texts[preferredLang];
    }

    return message.original_text;
  };

  const toggleMessageOriginal = (messageId: string) => {
    setMessageOriginalOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const hasTranslation = (message: Message) => {
    const preferredLang = profile?.preferred_language ?? language;
    return !!(
      preferredLang &&
      message.translated_texts &&
      message.translated_texts[preferredLang]
    );
  };

  const translateMissingMessages = async (opts?: {
    maxMessages?: number;
    batchSize?: number;
  }) => {
    if (!autoTranslate) return;

    const preferredLang = profile?.preferred_language ?? language;
    if (!preferredLang) return;

    const maxMessages = opts?.maxMessages ?? 25;
    const batchSize = opts?.batchSize ?? 5;

    const candidates = messages
      .filter((m) => m.sender_id !== user?.id)
      .filter((m) => Boolean(m.original_text))
      .filter((m) => m.sender_language && m.sender_language !== preferredLang)
      .filter((m) => !(m.translated_texts && m.translated_texts[preferredLang]))
      .filter((m) => !translatingMessageIdsRef.current.has(m.id))
      .slice(-maxMessages);

    if (candidates.length === 0) return;

    const batch = candidates.slice(0, batchSize);

    // Mark the whole batch as translating in one state update so the
    // shimmer appears for all of them simultaneously before any async work.
    setTranslatingMessageIds((prev) => {
      const next = new Set(prev);
      batch.forEach((m) => next.add(m.id));
      return next;
    });

    await Promise.allSettled(
      batch.map(async (m) => {
        translatingMessageIdsRef.current.add(m.id);
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const accessToken = session?.access_token;

          const { data, error } = await supabase.functions.invoke(
            "translate-message",
            {
              headers: accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : undefined,
              body: {
                text: m.original_text,
                sourceLanguage: m.sender_language,
                targetLanguages: [preferredLang],
              },
            }
          );

          const translated = data?.translations?.[preferredLang];

          if (error || !translated) {
            console.error("[ChatScreen] On-demand translation failed:", {
              messageId: m.id,
              error: error?.message ?? data?.error ?? "no translation",
            });

            if (!translationErrorShownRef.current) {
              translationErrorShownRef.current = true;
              toast({
                title: t("error"),
                description: String(
                  error?.message ?? data?.error ?? "Translation failed"
                ),
                variant: "destructive",
              });
            }
            return;
          }

          const nextTranslatedTexts = {
            ...(m.translated_texts || {}),
            [preferredLang]: translated,
          };

          const { error: updateError } = await supabase
            .from("messages")
            .update({ translated_texts: nextTranslatedTexts })
            .eq("id", m.id);

          if (updateError) {
            console.error(
              "[ChatScreen] Failed to persist translation:",
              updateError
            );
            return;
          }

          setMessages((prev) =>
            prev.map((pm) =>
              pm.id === m.id
                ? { ...pm, translated_texts: nextTranslatedTexts }
                : pm
            )
          );
        } finally {
          translatingMessageIdsRef.current.delete(m.id);
          setTranslatingMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(m.id);
            return next;
          });
        }
      })
    );
  };

  useEffect(() => {
    translateMissingMessages().catch((e) =>
      console.error("[ChatScreen] translateMissingMessages error:", e)
    );
  }, [
    autoTranslate,
    language,
    profile?.preferred_language,
    messages.length,
    user?.id,
  ]);

  const filteredMessages = messages.filter((message) => {
    if (!searchQuery.trim()) return true;

    const messageText = getMessageText(message).toLowerCase();
    const searchLower = searchQuery.toLowerCase();

    if (message.original_text.toLowerCase().includes(searchLower)) return true;
    if (messageText.includes(searchLower)) return true;
    if (message.sender?.name.toLowerCase().includes(searchLower)) return true;

    return false;
  });

  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const safeQuery = searchQuery.slice(0, 100).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safeQuery})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <mark
          key={index}
          className="bg-yellow-300 dark:bg-yellow-600 rounded px-0.5"
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  if (loading) {
    return (
      <div
        className={`${
          isMobile ? "min-h-screen" : "flex-1"
        } flex items-center justify-center`}
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div
      className={`${
        isMobile ? "safe-area-screen" : "flex-1"
      } flex flex-col bg-chat-bg overflow-hidden`}
    >
      <div
        className={`bg-card border-b flex-shrink-0 ${
          isMobile ? "pt-[max(env(safe-area-inset-top),0.75rem)]" : ""
        }`}
      >
        {/* Row 1: identity — back + avatar + name/status */}
        <div className="px-4 pt-3 pb-2 flex items-center gap-3">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/chats")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          {chatInfo?.is_group ? (
            <>
              <Avatar>
                <AvatarFallback>
                  <Users className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate">{chatInfo?.name}</h2>
                <button
                  onClick={() => setShowMembersDialog(true)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {allParticipants.length} {t("groupMembers")}
                </button>
              </div>
            </>
          ) : (
            <>
              <Avatar>
                <AvatarImage src={otherParticipant?.profile_image} />
                <AvatarFallback>{otherParticipant?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate">{otherParticipant?.name}</h2>
                {otherParticipant?.is_online && (
                  <p className="text-xs text-muted-foreground">{t("online")}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Row 2: action toolbar */}
        <div className="px-2 pb-2 flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMuteChat}
            title={isMuted ? t("unmuteChat") : t("muteChat")}
          >
            {isMuted ? <BellOff className="h-4 w-4 text-muted-foreground" /> : <Bell className="h-4 w-4" />}
          </Button>
          <Button
            variant={showSearch ? "default" : "ghost"}
            size="icon"
            onClick={() => {
              setShowSearch(!showSearch);
              if (showSearch) setSearchQuery("");
            }}
          >
            <Search className="h-4 w-4" />
          </Button>
          {chatInfo?.is_group && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMembersDialog(true)}
            >
              <Info className="h-5 w-5" />
            </Button>
          )}
          {!chatInfo?.is_group && otherParticipant?.id && (
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                if (isBlocked(otherParticipant.id)) {
                  setBlockBusy(true);
                  const ok = await unblock(otherParticipant.id);
                  setBlockBusy(false);
                  toast({
                    title: ok ? "User unblocked" : "Failed to unblock",
                    variant: ok ? "default" : "destructive",
                  });
                } else {
                  setConfirmBlockOpen(true);
                }
              }}
              disabled={blockBusy}
              title={isBlocked(otherParticipant.id) ? "Unblock user" : "Block user"}
            >
              {isBlocked(otherParticipant.id) ? (
                <ShieldCheck className="h-4 w-4 text-destructive" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMediaGallery(true)}
            title="Media, links & docs"
          >
            <Images className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowStarred(true)}
            title="Starred messages"
          >
            <Star className="h-4 w-4" />
          </Button>
          <div className="flex-1" />
          <Button
            variant={autoTranslate ? "default" : "ghost"}
            size="sm"
            onClick={() => {
              const next = !autoTranslate;
              setAutoTranslate(next);
              setMessageOriginalOverrides(new Set());
              if (next) {
                translateMissingMessages({ maxMessages: 10, batchSize: 3 }).catch(
                  (e) =>
                    console.error(
                      "[ChatScreen] translateMissingMessages(toggle) error:",
                      e
                    )
                );
              }
            }}
            title={autoTranslate ? t("showOriginal") : t("showTranslated")}
          >
            <Languages className="h-4 w-4 mr-2" />
            {autoTranslate ? t("showOriginal") : t("showTranslated")}
          </Button>
        </div>
      </div>

      {showSearch && (
        <div className="bg-card border-b px-4 py-3">
          <div className="max-w-2xl mx-auto relative">
            <Input
              placeholder={t("searchMessages")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-8"
              autoFocus
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {filteredMessages.length === 0
                ? t("noMessagesFound")
                : `${filteredMessages.length} ${t("search")}`}
            </p>
          )}
        </div>
      )}

      {/* Upsell banner — shown when trial has expired */}
      {!subscribed && !inTrial && (
        <div className="flex-shrink-0 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Languages className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 leading-tight">
                {t("translationPaused")}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 leading-snug">
                {t("translationPausedDesc")}
              </p>
            </div>
            <Button
              size="sm"
              className="flex-shrink-0 h-8 text-xs bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 text-white border-0"
              onClick={() => navigate("/settings")}
            >
              {t("unlockTranslation")}
            </Button>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1 p-4 w-full" ref={scrollAreaRef}>
        <div className="space-y-4 max-w-2xl mx-auto" ref={messagesListRef}>
          {filteredMessages.map((message) => {
            const isOwn = message.sender_id === user?.id;
            const messageText = getMessageText(message);
            const showingOriginal = messageOriginalOverrides.has(message.id);
            const canToggle = !isOwn && hasTranslation(message);

            return (
              <div
                key={message.id}
                data-message-id={message.id}
                className={`flex ${isOwn ? "justify-end" : "justify-start"} transition-colors duration-700 ${
                  highlightMessageId === message.id ? "bg-primary/20 rounded-lg" : ""
                }`}
              >
                <div
                  className={`flex gap-2 max-w-[70%] ${
                    isOwn ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  {!isOwn && (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={message.sender?.profile_image} />
                      <AvatarFallback>
                        {message.sender?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className="group/msg relative"
                    onDoubleClick={() => {
                      setReplyToMessage(message);
                      inputRef.current?.focus();
                    }}
                    onTouchStart={() => {
                      if (isOwn) {
                        longPressTimerRef.current = setTimeout(() => {
                          setLongPressMessage(message);
                        }, 500);
                      }
                    }}
                    onTouchEnd={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                      }
                    }}
                  >
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isOwn
                          ? "bg-sender-bubble text-sender-bubble-foreground"
                          : "bg-receiver-bubble text-receiver-bubble-foreground"
                      }`}
                    >
                      {message.reply_to_id && (() => {
                        const repliedMsg = messages.find(m => m.id === message.reply_to_id);
                        if (!repliedMsg) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              const el = document.querySelector(`[data-message-id="${repliedMsg.id}"]`);
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                setHighlightMessageId(repliedMsg.id);
                                setTimeout(() => setHighlightMessageId(null), 2000);
                              }
                            }}
                            className={`mb-2 w-full text-left border-l-2 border-primary/50 pl-2 py-1 rounded-r-md ${
                              isOwn ? "bg-primary/10" : "bg-muted/30"
                            }`}
                          >
                            <p className="text-xs font-medium opacity-70">
                              {repliedMsg.sender?.name || t("replyingTo")}
                            </p>
                            <p className="text-xs opacity-60 line-clamp-1">
                              {repliedMsg.original_text}
                            </p>
                          </button>
                        );
                      })()}
                      {message.attachment_url && (
                        <div className="mb-2">
                          <AttachmentDisplay
                            attachmentUrl={message.attachment_url}
                            getFileType={getFileType}
                            isOwnMessage={isOwn}
                            senderLanguage={message.sender_language}
                          />
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {highlightText(messageText)}
                      </p>
                      <LinkPreview text={messageText} isOwn={isOwn} />
                      {!isOwn && translatingMessageIds.has(message.id) && !hasTranslation(message) && (
                        <div className="mt-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 w-fit bg-black/10 dark:bg-white/10 animate-pulse">
                          <div className="h-3 w-3 rounded-full bg-current opacity-40" />
                          <div className="h-2.5 w-24 rounded-full bg-current opacity-30" />
                        </div>
                      )}
                      {canToggle && (
                        <button
                          type="button"
                          onClick={() => toggleMessageOriginal(message.id)}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 active:scale-95 transition-all select-none"
                        >
                          <Languages className="h-3 w-3 flex-shrink-0" />
                          {showingOriginal
                            ? t("showTranslated")
                            : message.sender_language
                              ? t("translatedFrom").replace("{language}", LANGUAGE_NAMES[message.sender_language] ?? message.sender_language)
                              : t("showOriginal")}
                        </button>
                      )}
                    </div>
                    <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex gap-1 ${
                        isOwn ? "-left-24" : "-right-24"
                      }`}>
                      <button
                        type="button"
                        onClick={() => {
                          setReplyToMessage(message);
                          inputRef.current?.focus();
                        }}
                        className="p-1 rounded-full bg-muted hover:bg-muted/80"
                        title={t("reply")}
                      >
                        <Reply className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setForwardMessage(message)}
                        className="p-1 rounded-full bg-muted hover:bg-muted/80"
                        title={t("forward")}
                      >
                        <Forward className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleStarMessage(message.id)}
                        className="p-1 rounded-full bg-muted hover:bg-muted/80"
                        title={starredIds.has(message.id) ? "Unstar" : "Star"}
                      >
                        <Star
                          className={`h-3.5 w-3.5 ${
                            starredIds.has(message.id)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-muted-foreground"
                          }`}
                        />
                      </button>
                      {isOwn && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessage(message);
                              setShowEditDialog(true);
                            }}
                            className="p-1 rounded-full bg-muted hover:bg-muted/80"
                            title={t("edit")}
                          >
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeletingMessage(message);
                              setShowDeleteDialog(true);
                            }}
                            className="p-1 rounded-full bg-muted hover:bg-destructive/20"
                            title={t("delete")}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </button>
                        </>
                      )}
                    </div>
                    <p
                      className={`text-xs text-muted-foreground mt-1 flex items-center gap-0.5 ${
                        isOwn ? "justify-end" : "justify-start"
                      }`}
                    >
                      {formatDistanceToNow(new Date(message.created_at), {
                        addSuffix: true,
                      })}
                      {isOwn && <MessageStatus isRead={!!message.is_read} />}
                    </p>
                    <MessageReactions
                      messageId={message.id}
                      reactions={message.reactions || []}
                      onReactionUpdate={fetchMessages}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {typingUsers.length > 0 && (
            <div className="flex items-start gap-2 pl-10">
              <div className="bg-receiver-bubble text-receiver-bubble-foreground rounded-2xl px-4 py-2.5 flex items-center gap-2 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.15s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.3s]" />
                </div>
                <span className="text-xs text-muted-foreground">
                  {typingUsers.length === 1
                    ? `${typingUsers[0]} ${t("typing")}`
                    : typingUsers.length === 2
                    ? `${typingUsers[0]} & ${typingUsers[1]} ${t("typingPlural")}`
                    : `${typingUsers[0]}, ${typingUsers[1]} ${t("andOthers").replace("{count}", String(typingUsers.length - 2))} ${t("typingPlural")}`
                  }
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {!chatInfo?.is_group && otherParticipant?.id && isBlocked(otherParticipant.id) ? (
        <div
          className={`bg-muted border-t p-4 ${
            isMobile ? "pb-[max(env(safe-area-inset-bottom),1rem)]" : ""
          } flex-shrink-0 text-center text-sm text-muted-foreground flex items-center justify-center gap-2`}
        >
          <Ban className="h-4 w-4" />
          You blocked this user.{" "}
          <button
            type="button"
            className="text-primary underline hover:no-underline font-medium"
            onClick={async () => {
              setBlockBusy(true);
              const ok = await unblock(otherParticipant.id);
              setBlockBusy(false);
              toast({
                title: ok ? "User unblocked" : "Failed to unblock",
                variant: ok ? "default" : "destructive",
              });
            }}
          >
            Tap to unblock
          </button>
        </div>
      ) : (
      <form
        onSubmit={sendMessage}
        className={`bg-card border-t p-4 ${
          isMobile ? "pb-[max(env(safe-area-inset-bottom),1rem)]" : ""
        } flex-shrink-0 max-h-[40vh] overflow-y-auto`}
      >
        <div className="max-w-2xl mx-auto space-y-2">
          {replyToMessage && (
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <div className="flex-1 border-l-2 border-primary pl-2 min-w-0">
                <p className="text-xs font-medium text-primary truncate">
                  {replyToMessage.sender?.name || t("replyingTo")}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {replyToMessage.original_text}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => setReplyToMessage(null)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {selectedFiles.length} file
                  {selectedFiles.length > 1 ? "s" : ""}
                </span>
                {uploading && <span>{uploadProgress}%</span>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-24 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    {file.type.startsWith("image/") ? (
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeFile(index)}
                          className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <FileIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-xs flex-1 truncate">
                          {file.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(index)}
                          className="h-6 w-6"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.zip,.json,.xml,.rtf"
            />
            {isNative && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleNativeImagePick}
                disabled={sending || uploading || cameraLoading}
              >
                <Camera className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploading}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            {isRecording ? (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-sm text-destructive animate-pulse">
                  <span className="w-2 h-2 rounded-full bg-destructive" />
                  Recording...
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={cancelRecording}
                  disabled={isUploadingVoice}
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  onClick={stopRecording}
                  disabled={isUploadingVoice}
                >
                  {isUploadingVoice ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ) : (
              <>
                <Input
                  ref={inputRef}
                  placeholder={t("typeMessage")}
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  disabled={sending || uploading}
                  className="flex-1"
                />
                {newMessage.trim() || selectedFiles.length > 0 ? (
                  /* Send button — shown only when there is content to send */
                  <Button
                    type="submit"
                    size="icon"
                    disabled={sending || uploading}
                  >
                    {sending || uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                ) : (
                  /* Mic button — primary CTA when the text field is empty */
                  <Button
                    type="button"
                    size="icon"
                    className="h-10 w-10 rounded-full flex-shrink-0"
                    onClick={startRecording}
                    disabled={sending || uploading || isUploadingVoice}
                    title="Record voice message"
                  >
                    {isUploadingVoice ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mic className="h-5 w-5" />
                    )}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </form>
      )}

      <GroupMembersDialog
        open={showMembersDialog}
        onOpenChange={setShowMembersDialog}
        chatId={chatId || ""}
        isGroup={chatInfo?.is_group || false}
        members={allParticipants}
        onMembersUpdate={fetchAllParticipants}
      />
      <MediaGalleryDialog
        open={showMediaGallery}
        onOpenChange={setShowMediaGallery}
        chatId={chatId || ""}
      />
      <StarredMessagesDialog
        open={showStarred}
        onOpenChange={(open) => {
          setShowStarred(open);
          if (!open) void fetchStarredForChat();
        }}
        chatId={chatId || undefined}
        onJump={(_chatId, messageId) => {
          setHighlightMessageId(messageId);
          const el = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement | null;
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          setTimeout(() => setHighlightMessageId(null), 2000);
        }}
      />
      <AlertDialog open={confirmBlockOpen} onOpenChange={setConfirmBlockOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {otherParticipant?.name || "user"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Blocked users will no longer be able to send you messages. You can unblock them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!otherParticipant?.id) return;
                setBlockBusy(true);
                const ok = await block(otherParticipant.id);
                setBlockBusy(false);
                setConfirmBlockOpen(false);
                toast({
                  title: ok ? "User blocked" : "Failed to block",
                  variant: ok ? "default" : "destructive",
                });
              }}
            >
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {forwardMessage && (
        <ForwardMessageDialog
          open={!!forwardMessage}
          onOpenChange={(open) => !open && setForwardMessage(null)}
          messageText={forwardMessage.original_text}
          attachmentUrl={forwardMessage.attachment_url}
          senderName={forwardMessage.sender?.name}
        />
      )}
      {/* Long-press action sheet for mobile */}
      {longPressMessage && (
        <Dialog open={!!longPressMessage} onOpenChange={(open) => !open && setLongPressMessage(null)}>
          <DialogContent className="sm:max-w-xs">
            <DialogHeader>
              <DialogTitle className="text-sm">{t("editMessage").split(" ")[0]}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => {
                  toggleStarMessage(longPressMessage.id);
                  setLongPressMessage(null);
                }}
              >
                <Star
                  className={`h-4 w-4 ${
                    starredIds.has(longPressMessage.id)
                      ? "fill-yellow-400 text-yellow-400"
                      : ""
                  }`}
                />
                {starredIds.has(longPressMessage.id) ? "Unstar message" : "Star message"}
              </Button>
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => {
                  setEditingMessage(longPressMessage);
                  setShowEditDialog(true);
                  setLongPressMessage(null);
                }}
              >
                <Pencil className="h-4 w-4" />
                {t("editMessage")}
              </Button>
              <Button
                variant="outline"
                className="justify-start gap-2 text-destructive hover:text-destructive"
                onClick={() => {
                  setDeletingMessage(longPressMessage);
                  setShowDeleteDialog(true);
                  setLongPressMessage(null);
                }}
              >
                <Trash2 className="h-4 w-4" />
                {t("deleteMessage")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Edit/Delete dialogs */}
      {editingMessage && (
        <MessageActions
          message={editingMessage}
          chatId={chatId || ""}
          allParticipants={allParticipants}
          onMessageUpdated={() => {
            fetchMessages();
            setEditingMessage(null);
          }}
          showDeleteDialog={false}
          setShowDeleteDialog={() => {}}
          showEditDialog={showEditDialog}
          setShowEditDialog={(show) => {
            setShowEditDialog(show);
            if (!show) setEditingMessage(null);
          }}
        />
      )}
      {deletingMessage && (
        <MessageActions
          message={deletingMessage}
          chatId={chatId || ""}
          allParticipants={allParticipants}
          onMessageUpdated={() => {
            fetchMessages();
            setDeletingMessage(null);
          }}
          showDeleteDialog={showDeleteDialog}
          setShowDeleteDialog={(show) => {
            setShowDeleteDialog(show);
            if (!show) setDeletingMessage(null);
          }}
          showEditDialog={false}
          setShowEditDialog={() => {}}
        />
      )}
    </div>
  );
};

export default ChatScreen;
